import type { AxiosError, AxiosResponse } from 'axios'
import type { Dispatch } from 'redux'

import { AddUSerFormActionTypes } from './actionTypes'
import type { ErrorUsers, UserType } from './reducer'
import { arrayReducerActionWrapper } from './arrayReducerActions'
import {
  getAddUserSuccessMessage,
  submitUserInvitations,
} from './helpers'
import { resolveAddUserErrorMessage } from './utils'
import { RequestFailureMessage } from '../../utils/messagesContants'
import { errorLogger, type LoggerGlobalState } from '../../utils/errorLogger'
import { startLoading, stopLoading } from '../loading/actions'
import { LoadingTypes } from '../loading/reducer'
import { updateAppAlert } from '../appAlert/actions'
import { AuthProvider } from '../userProfile/userProfileTypes'
import { PartnerType } from '../../types/partner'
import { getUsers } from '../userList/actions'

export { AddUSerFormActionTypes }

type AddUsersThunkState = {
  userList: Record<string, { pageSize?: number } | undefined>
}

export const showAddUserForm = () => ({
  type: AddUSerFormActionTypes.SHOW_ADD_USER_FORM,
})

export const hideAddUserForm = () => ({
  type: AddUSerFormActionTypes.HIDE_ADD_USER_FORM,
})

export const addUserErrorData = (errors: ErrorUsers[]) => ({
  type: AddUSerFormActionTypes.ADD_USER_ERROR_DATA,
  payload: errors,
})

export const startUserDataLoading = () => ({
  type: AddUSerFormActionTypes.START_USER_DATA_LOADING,
})

export const endUserDataLoading = () => ({
  type: AddUSerFormActionTypes.END_USER_DATA_LOADING,
})

export const cleanUpUserData = () => ({
  type: AddUSerFormActionTypes.ADD_USER_DATA_CLEANUP,
})

export const submitAddNewUsersData =
  (
    data: UserType[],
    partnerId: string,
    authProvider: `${AuthProvider}`,
    isAdmin = false,
    callBackAfterSuccess?: () => void,
    closeModal = true,
    partnerType: `${PartnerType}` = PartnerType.User
  ) =>
  async (dispatch: Dispatch, getState: () => AddUsersThunkState) => {
    await dispatch(startLoading(LoadingTypes.ADD_NEW_USER))
    try {
      await submitUserInvitations({
        data,
        partnerId,
        authProvider,
        isAdmin,
      })

      await dispatch(
        updateAppAlert({
          message: getAddUserSuccessMessage(data),
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      if (closeModal) {
        await dispatch(cleanUpUserData())
        await dispatch(hideAddUserForm())
      }
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const responseError = errorResponse.data as {
        message?: string
        errors?: string[]
      }
      const errMess =
        responseError?.message ??
        (Array.isArray(responseError?.errors)
          ? responseError.errors[0]
          : undefined) ??
        RequestFailureMessage
      await dispatch(
        updateAppAlert({
          message: resolveAddUserErrorMessage(errMess, RequestFailureMessage),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState: globalState as LoggerGlobalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      if (closeModal) {
        await dispatch(hideAddUserForm())
      }
    } finally {
      if (!isAdmin) {
        const state = getState()
        const currentUserListState = state.userList[partnerType]
        const currentPageSize = currentUserListState?.pageSize || 10
        dispatch(
          getUsers(
            {
              partnerId,
              pageNumber: 1,
              pageSize: currentPageSize,
              authProvider: authProvider as AuthProvider,
            },
            partnerType as PartnerType
          ) as never
        )
      } else {
        callBackAfterSuccess && callBackAfterSuccess()
      }
      dispatch(stopLoading(LoadingTypes.ADD_NEW_USER))
    }
  }

const arrayReducerActions = arrayReducerActionWrapper<UserType>('addNewUsers')
export const updateRowForAddUser = arrayReducerActions.updateRow
export const addRowForAddUser = arrayReducerActions.addRow
export const removeRowForAddUser = arrayReducerActions.removeRow
